import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

export type CourierPalette = {
  color: string;
  emissive: string;
  trail: string;
};

/** Low-poly hoverbike courier — readable at speed, tinted by palette. */
export function CourierModel({
  skin,
  ghost = false,
}: {
  skin: CourierPalette;
  ghost?: boolean;
}) {
  const thruster = useRef<THREE.Mesh>(null);
  const mats = useMemo(
    () => ({
      body: new THREE.MeshStandardMaterial({
        color: skin.color,
        emissive: skin.emissive,
        emissiveIntensity: ghost ? 0.22 : 0.95,
        metalness: 0.55,
        roughness: 0.28,
        transparent: true,
        opacity: ghost ? 0.16 : 1,
        depthWrite: !ghost,
        wireframe: ghost,
      }),
      dark: new THREE.MeshStandardMaterial({
        color: "#0F172A",
        metalness: 0.45,
        roughness: 0.4,
        transparent: true,
        opacity: ghost ? 0.14 : 1,
        depthWrite: !ghost,
        wireframe: ghost,
      }),
      glass: new THREE.MeshStandardMaterial({
        color: "#67E8F9",
        emissive: "#22D3EE",
        emissiveIntensity: ghost ? 0.35 : 1.2,
        metalness: 0.8,
        roughness: 0.15,
        transparent: true,
        opacity: ghost ? 0.2 : 0.85,
        depthWrite: !ghost,
        wireframe: ghost,
      }),
      trail: new THREE.MeshStandardMaterial({
        color: skin.trail,
        emissive: skin.trail,
        emissiveIntensity: 2.4,
        transparent: true,
        opacity: ghost ? 0 : 0.9,
        toneMapped: false,
        depthWrite: false,
      }),
      pad: new THREE.MeshStandardMaterial({
        color: "#E879F9",
        emissive: "#A21CAF",
        emissiveIntensity: ghost ? 0.3 : 1.35,
        toneMapped: false,
        transparent: true,
        opacity: ghost ? 0.22 : 1,
        depthWrite: !ghost,
        wireframe: ghost,
      }),
    }),
    [skin.color, skin.emissive, skin.trail, ghost],
  );

  useFrame(({ clock }) => {
    if (thruster.current) {
      const s = 0.85 + Math.sin(clock.elapsedTime * 28) * 0.18;
      thruster.current.scale.set(s, 1 + (1 - s) * 0.4, s);
    }
  });

  return (
    <group>
      {/* hull */}
      <mesh castShadow material={mats.body} position={[0, 0.08, 0]}>
        <boxGeometry args={[0.98, 0.24, 1.72]} />
      </mesh>
      {/* nose */}
      <mesh castShadow material={mats.body} position={[0, 0.12, -0.86]} rotation={[0.15, 0, 0]}>
        <boxGeometry args={[0.66, 0.18, 0.46]} />
      </mesh>
      {/* cockpit */}
      <mesh castShadow material={mats.dark} position={[0, 0.38, -0.06]}>
        <boxGeometry args={[0.56, 0.32, 0.64]} />
      </mesh>
      <mesh material={mats.glass} position={[0, 0.46, -0.26]}>
        <boxGeometry args={[0.5, 0.18, 0.26]} />
      </mesh>
      {/* wings */}
      <mesh castShadow material={mats.body} position={[-0.66, 0.06, 0.18]}>
        <boxGeometry args={[0.4, 0.07, 0.82]} />
      </mesh>
      <mesh castShadow material={mats.body} position={[0.66, 0.06, 0.18]}>
        <boxGeometry args={[0.4, 0.07, 0.82]} />
      </mesh>
      {/* front light */}
      <mesh material={mats.glass} position={[0, 0.14, -1.12]}>
        <boxGeometry args={[0.42, 0.09, 0.09]} />
      </mesh>
      {/* thruster */}
      <mesh
        ref={thruster}
        name="trail"
        material={mats.trail}
        position={[0, 0.1, 1.12]}
        rotation={[Math.PI / 2, 0, 0]}
      >
        <coneGeometry args={[0.22, 0.88, 12]} />
      </mesh>
      {/* hover rings */}
      {[-0.4, 0.4].map((x) => (
        <mesh
          key={x}
          material={mats.pad}
          position={[x, -0.08, 0.05]}
          rotation={[Math.PI / 2, 0, 0]}
        >
          <torusGeometry args={[0.2, 0.04, 8, 18]} />
        </mesh>
      ))}
    </group>
  );
}

/** Ground disc so traffic pops on a dark road (skipped by collision AABB). */
export function makeLaneGlow(color: string, radius = 0.62) {
  const glow = new THREE.Mesh(
    new THREE.CircleGeometry(radius, 18),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.42,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.03;
  glow.name = "__glow";
  return glow;
}

export function makeRivalMesh() {
  const g = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.92, 0.3, 1.62),
    new THREE.MeshStandardMaterial({
      color: "#FB7185",
      emissive: "#E11D48",
      emissiveIntensity: 1.15,
      metalness: 0.5,
      roughness: 0.32,
    }),
  );
  const cab = new THREE.Mesh(
    new THREE.BoxGeometry(0.52, 0.32, 0.52),
    new THREE.MeshStandardMaterial({ color: "#1F2937" }),
  );
  cab.position.set(0, 0.28, -0.1);
  const wingL = new THREE.Mesh(
    new THREE.BoxGeometry(0.34, 0.06, 0.68),
    new THREE.MeshStandardMaterial({
      color: "#FB7185",
      emissive: "#BE123C",
      emissiveIntensity: 0.6,
    }),
  );
  wingL.position.set(-0.6, 0.05, 0.12);
  const wingR = wingL.clone();
  wingR.position.x = 0.6;
  const flame = new THREE.Mesh(
    new THREE.ConeGeometry(0.18, 0.62, 10),
    new THREE.MeshStandardMaterial({
      color: "#FDA4AF",
      emissive: "#F43F5E",
      emissiveIntensity: 2,
      transparent: true,
      opacity: 0.85,
      toneMapped: false,
    }),
  );
  flame.position.set(0, 0.08, 1.12);
  flame.rotation.x = Math.PI / 2;
  g.add(body, cab, wingL, wingR, flame, makeLaneGlow("#FB7185", 0.7));
  return g;
}

export function makeBarrierMesh() {
  const g = new THREE.Group();
  const bar = new THREE.Mesh(
    new THREE.BoxGeometry(1.32, 1.02, 0.38),
    new THREE.MeshStandardMaterial({
      color: "#FACC15",
      emissive: "#D97706",
      emissiveIntensity: 1.05,
      metalness: 0.2,
      roughness: 0.4,
    }),
  );
  const stripe = new THREE.Mesh(
    new THREE.BoxGeometry(1.36, 0.28, 0.42),
    new THREE.MeshBasicMaterial({ color: "#111827" }),
  );
  stripe.position.y = 0.18;
  const stripe2 = stripe.clone();
  stripe2.position.y = -0.22;
  const lip = new THREE.Mesh(
    new THREE.BoxGeometry(1.36, 0.1, 0.44),
    new THREE.MeshBasicMaterial({ color: "#FDE68A", toneMapped: false }),
  );
  lip.position.y = 0.52;
  const postL = new THREE.Mesh(
    new THREE.CylinderGeometry(0.07, 0.07, 1.08, 8),
    new THREE.MeshStandardMaterial({ color: "#1C1917" }),
  );
  postL.position.set(-0.54, -0.05, 0);
  const postR = postL.clone();
  postR.position.x = 0.54;
  g.add(bar, stripe, stripe2, lip, postL, postR, makeLaneGlow("#FACC15", 0.72));
  return g;
}

export function makeCrateMesh() {
  const g = new THREE.Group();
  const box = new THREE.Mesh(
    new THREE.BoxGeometry(1.12, 1.12, 1.12),
    new THREE.MeshStandardMaterial({
      color: "#EA580C",
      emissive: "#9A3412",
      emissiveIntensity: 0.85,
      metalness: 0.2,
      roughness: 0.55,
    }),
  );
  const bandH = new THREE.Mesh(
    new THREE.BoxGeometry(1.2, 0.16, 1.2),
    new THREE.MeshBasicMaterial({ color: "#111827" }),
  );
  const bandV = new THREE.Mesh(
    new THREE.BoxGeometry(0.16, 1.2, 1.2),
    new THREE.MeshBasicMaterial({ color: "#111827" }),
  );
  const rim = new THREE.Mesh(
    new THREE.BoxGeometry(1.16, 1.16, 1.16),
    new THREE.MeshBasicMaterial({
      color: "#FDBA74",
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  g.add(box, bandH, bandV, rim, makeLaneGlow("#FB923C", 0.7));
  return g;
}

/**
 * Tall hauler — jump can't clear it; dodge only.
 * Faces the camera (+Z) so headlights/cab read as "wall", not a dark cube.
 */
export function makeTruckMesh() {
  const g = new THREE.Group();
  const hull = new THREE.MeshStandardMaterial({
    color: "#FFF7ED",
    emissive: "#FDBA74",
    emissiveIntensity: 0.55,
    metalness: 0.18,
    roughness: 0.42,
  });
  const cabPaint = new THREE.MeshStandardMaterial({
    color: "#2563EB",
    emissive: "#1D4ED8",
    emissiveIntensity: 0.55,
    metalness: 0.3,
    roughness: 0.4,
  });
  const dark = new THREE.MeshStandardMaterial({
    color: "#0F172A",
    metalness: 0.5,
    roughness: 0.35,
  });
  const caution = new THREE.MeshStandardMaterial({
    color: "#F97316",
    emissive: "#EA580C",
    emissiveIntensity: 1.25,
    toneMapped: false,
  });
  const lamp = new THREE.MeshBasicMaterial({
    color: "#FEF3C7",
    toneMapped: false,
  });

  const trailer = new THREE.Mesh(new THREE.BoxGeometry(1.08, 1.05, 1.42), hull);
  trailer.position.set(0, 0.86, -0.32);
  const sideL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.85, 1.36), caution);
  const sideR = sideL.clone();
  sideL.position.set(-0.56, 0.9, -0.32);
  sideR.position.set(0.56, 0.9, -0.32);
  const cab = new THREE.Mesh(new THREE.BoxGeometry(1.1, 1.38, 0.95), cabPaint);
  cab.position.set(0, 0.96, 0.82);
  const glass = new THREE.Mesh(
    new THREE.BoxGeometry(0.96, 0.5, 0.1),
    new THREE.MeshStandardMaterial({
      color: "#67E8F9",
      emissive: "#22D3EE",
      emissiveIntensity: 1.35,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
    }),
  );
  glass.position.set(0, 1.28, 1.3);
  const roofGlass = new THREE.Mesh(
    new THREE.BoxGeometry(0.86, 0.08, 0.42),
    new THREE.MeshBasicMaterial({ color: "#67E8F9", toneMapped: false }),
  );
  roofGlass.position.set(0, 1.68, 0.95);
  const grill = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.4, 0.08), dark);
  grill.position.set(0, 0.58, 1.3);
  const bumper = new THREE.Mesh(new THREE.BoxGeometry(1.16, 0.16, 0.18), dark);
  bumper.position.set(0, 0.28, 1.24);
  const lightL = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.16, 0.1), lamp);
  const lightR = lightL.clone();
  lightL.position.set(-0.34, 0.48, 1.32);
  lightR.position.set(0.34, 0.48, 1.32);
  const roof = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.14, 0.16), caution);
  const roofL = roof.clone();
  const roofR = roof.clone();
  roof.position.set(0, 1.72, 0.72);
  roofL.position.set(-0.34, 1.72, 0.72);
  roofR.position.set(0.34, 1.72, 0.72);

  const wheelMat = new THREE.MeshStandardMaterial({ color: "#111827" });
  const mkWheel = (x: number, z: number) => {
    const w = new THREE.Mesh(
      new THREE.CylinderGeometry(0.22, 0.22, 0.18, 10),
      wheelMat,
    );
    w.rotation.z = Math.PI / 2;
    w.position.set(x, 0.22, z);
    return w;
  };
  g.add(
    trailer,
    sideL,
    sideR,
    cab,
    glass,
    roofGlass,
    grill,
    bumper,
    lightL,
    lightR,
    roof,
    roofL,
    roofR,
    mkWheel(-0.5, 0.95),
    mkWheel(0.5, 0.95),
    mkWheel(-0.5, -0.15),
    mkWheel(0.5, -0.15),
    mkWheel(-0.5, -0.85),
    mkWheel(0.5, -0.85),
    makeLaneGlow("#F97316", 0.78),
  );
  return g;
}

/** Volcano fissure — must jump; looks like a glowing pit in the lane. */
export function makeHoleMesh() {
  const g = new THREE.Group();
  const pit = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.7, 0.35, 16),
    new THREE.MeshStandardMaterial({
      color: "#1C1917",
      emissive: "#7C2D12",
      emissiveIntensity: 0.9,
      roughness: 0.9,
    }),
  );
  pit.position.y = -0.12;
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(0.62, 0.08, 8, 20),
    new THREE.MeshStandardMaterial({
      color: "#F97316",
      emissive: "#EA580C",
      emissiveIntensity: 1.6,
      toneMapped: false,
    }),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.02;
  const glow = new THREE.Mesh(
    new THREE.CircleGeometry(0.4, 16),
    new THREE.MeshBasicMaterial({
      color: "#FB923C",
      transparent: true,
      opacity: 0.55,
    }),
  );
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.04;
  g.add(pit, rim, glow);
  return g;
}

export function makeCoinMesh() {
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(0.32, 0.32, 0.1, 20),
    new THREE.MeshStandardMaterial({
      color: "#FBBF24",
      emissive: "#B45309",
      emissiveIntensity: 1.2,
      metalness: 0.8,
      roughness: 0.18,
    }),
  );
  mesh.rotation.x = Math.PI / 2;
  return mesh;
}

/** Jump token — cyan chevron / boost pad, distinct from gold coins. */
export function makeJumpTokenMesh() {
  const g = new THREE.Group();
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.38, 0.07, 10, 24),
    new THREE.MeshStandardMaterial({
      color: "#22D3EE",
      emissive: "#0891B2",
      emissiveIntensity: 1.6,
      metalness: 0.5,
      roughness: 0.25,
      toneMapped: false,
    }),
  );
  ring.rotation.x = Math.PI / 2;
  const arrow = new THREE.Mesh(
    new THREE.ConeGeometry(0.22, 0.42, 4),
    new THREE.MeshStandardMaterial({
      color: "#67E8F9",
      emissive: "#22D3EE",
      emissiveIntensity: 1.8,
      toneMapped: false,
    }),
  );
  arrow.position.y = 0.12;
  g.add(ring, arrow);
  return g;
}
